import { DataDate } from "./data_date";

export type SeriesValue = [string, number] | [string, number, boolean]; // [ymd, stats] or [ymd, stats, is_projected]
type TimeLabelFormat = "monthday" | "month" | "year";

class Series {
  private _dates: DataDate[];
  private _stats: number[];
  private _isProjected: boolean[]; // 各データポイントが予測値かどうか
  name: string;
  origin?: DataDate;

  constructor(values: SeriesValue[], name: string, origin?: DataDate) {
    this._dates = values.map(([ymd, _s]) => DataDate.parseYMD(ymd)!);
    this._stats = values.map(([_ymd, s]) => s);
    // is_projectedフラグが提供されている場合は使用、そうでなければfalse
    this._isProjected = values.map((val) => (val.length >= 3 ? Boolean(val[2]) : false));
    this.name = name;
    this.origin = origin;
  }

  isProjected(index: number): boolean {
    return this._isProjected[index] || false;
  }

  isEmpty(): boolean {
    return this._dates.length === 0;
  }

  size(): number {
    return this._dates.length;
  }

  dates(): DataDate[] {
    return this._dates;
  }

  stats(): number[] {
    return this._stats;
  }

  originRelativeTimes(): number[] | null {
    if (!this.origin) return null;
    const org = this.origin.unixTime();
    return this._dates.map((d) => d.unixTime() - org);
  }
}

class SeriesSet {
  public list: Series[];
  private globalOrg: number | null;

  constructor(list: Series[] = []) {
    this.list = list;
    this.globalOrg = null;
  }

  toArray(): Series[] {
    return Array.from(this.list);
  }

  timeLabel(t: number, timeLabelFormat: TimeLabelFormat = "monthday"): string {
    // We cannot display year, because multiple years may be overlapped
    const dataDate = this.getDataDate(t);
    switch (timeLabelFormat) {
      case "month":
        return dataDate.m();
      case "year":
        return dataDate.y();
      default:
        return dataDate.md();
    }
  }

  public getDataDate(t: number): DataDate {
    const org = this.globalOriginTime();
    return new DataDate(new Date(org + t), "weekly");
  }

  private globalOriginTime(): number {
    if (this.globalOrg !== null) return this.globalOrg;
    const seriesWithOrigin = this.list.find((series) => series.origin);
    this.globalOrg = seriesWithOrigin?.origin?.unixTime() || 0;
    return this.globalOrg;
  }

  hasNoData(): boolean {
    return this.list.every((series) => series.isEmpty());
  }

  isEmpty(): boolean {
    return this.list.length === 0;
  }

  size(): number {
    return this.list.length;
  }

  hasKey(name: string): boolean {
    return this.list.some((series) => series.name === name);
  }

  get(index: number): Series {
    return this.list[index];
  }

  add(series: Series): void {
    if (this.hasKey(series.name)) return;
    this.list.push(series);
  }

  remove(series: Series): number | null {
    for (let idx = 0; idx < this.list.length; idx++) {
      if (this.list[idx] === series) {
        this.list.splice(idx, 1);
        return idx;
      }
    }
    return null;
  }

  each(f: (series: Series, index: number) => void): void {
    this.list.forEach(f);
  }

  map<T>(f: (series: Series, index: number) => T): T[] {
    return this.list.map(f);
  }
}

export { Series, SeriesSet };
